import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useKinderConnectCaptureGate } from '@famly/stat_kinder-connect-capture_business-logic';
import translate from 'signin-app/helpers/translate';
import { pinHolder } from 'signin-app/pin/selectors';
import { ConfirmButton, ActionButton as CancelButton, BottomBarContainer } from 'signin-app/components/action-buttons';
import { isPinApp, getSiteId } from 'signin-app/login/selectors';
import RoutesMap from 'signin-app/routes/routes-map';
import * as ChildSelectors from 'signin-app/child/selectors';
import * as GroupSelectors from 'signin-app/groups/selectors';
import useTypedSelector from 'signin-app/components/hooks/use-typed-selector';
import { isBeforeNow } from 'signin-app/groups/helpers';
import { useUpdateError } from '@famly/stat_ts-utils_global-messages';
import { defaultErrorDuration } from 'web-app/react/global-event/helpers/constants';
import { useDisplayName } from 'signin-app/helpers/use-display-name';
import * as Selectors from './selectors';
import * as Actions from './actions';
export const CheckinSubmit = (props) => {
    const { accessToken, child } = props;
    const updateError = useUpdateError();
    const selectedPickupRelationId = useTypedSelector(Selectors.selectedPickupRelationId);
    const selectedHours = useTypedSelector(Selectors.selectedHours);
    const selectedMinutes = useTypedSelector(Selectors.selectedMinutes);
    const pickupPersonName = useTypedSelector(state => Selectors.pickupPersonName(state, props));
    const isCheckingIn = useTypedSelector(Selectors.isCheckingIn);
    const pin = useTypedSelector(pinHolder);
    const { childId: childIdFromParams } = useParams();
    const canCheckIn = useTypedSelector(state => Selectors.canCheckIn(state, { childId: childIdFromParams }));
    const isPin = useTypedSelector(isPinApp);
    const dispatch = useDispatch();
    const timezone = useTypedSelector(GroupSelectors.timezone);
    const siteId = useTypedSelector(getSiteId);
    const displayName = useDisplayName(child === null || child === void 0 ? void 0 : child.name.firstName, child === null || child === void 0 ? void 0 : child.name.lastName);
    const nextPersonUrl = useTypedSelector(state => ChildSelectors.nextPersonUrl(state, props));
    const { isLoading: isKCCaptureLoading, runOrCapture: runOrCaptureKC } = useKinderConnectCaptureGate([child.id], siteId);
    const nextPersonLink = React.useMemo(() => {
        if (isPin) {
            return nextPersonUrl === RoutesMap.pin ? null : nextPersonUrl;
        }
        return null;
    }, [isPin, nextPersonUrl]);
    const dispatchCheckin = React.useCallback((kcValues) => {
        const pickupName = selectedPickupRelationId ? '' : pickupPersonName;
        const successMessage = translate(child.checkedIn ? 'updateSignInSuccess' : 'signInSuccess', {
            personName: displayName,
        });
        dispatch(Actions.checkInChild.action({
            accessToken,
            childId: child.id,
            pickupRelationId: selectedPickupRelationId,
            selectedHours,
            selectedMinutes,
            newPickupRelationName: pickupName,
            successMessage,
            groupId: child.groupId,
            pin,
            isCheckedIn: child.checkedIn,
            kinderConnectPhoto: kcValues === null || kcValues === void 0 ? void 0 : kcValues.photoBase64,
            kinderConnectSignature: kcValues === null || kcValues === void 0 ? void 0 : kcValues.signatureBase64,
            kinderConnectLatitude: kcValues === null || kcValues === void 0 ? void 0 : kcValues.latitude,
            kinderConnectLongitude: kcValues === null || kcValues === void 0 ? void 0 : kcValues.longitude,
        }));
    }, [
        accessToken,
        child,
        displayName,
        dispatch,
        pin,
        pickupPersonName,
        selectedHours,
        selectedMinutes,
        selectedPickupRelationId,
    ]);
    const handleChildCheckin = React.useCallback(() => {
        // Whether capture is required is still in flight -- proceeding here risks completing a
        // check-in that should have required capture. Block until the backend flag resolves.
        if (isKCCaptureLoading) {
            return;
        }
        const pastPickupTimeSelected = isBeforeNow(timezone, selectedHours, selectedMinutes);
        if (pastPickupTimeSelected) {
            updateError(translate('updatePickupTimeError'), { duration: defaultErrorDuration });
            return;
        }
        runOrCaptureKC(dispatchCheckin);
    }, [dispatchCheckin, isKCCaptureLoading, runOrCaptureKC, selectedHours, selectedMinutes, timezone, updateError]);
    return (_jsxs(BottomBarContainer, { children: [nextPersonLink ? _jsx(CancelButton, { text: translate('skip'), link: nextPersonLink }) : null, _jsx(ConfirmButton, { type: 'signIn', isLoading: isCheckingIn || isKCCaptureLoading, onClick: handleChildCheckin, text: child.checkedIn ? translate('updatePickup') : translate('signIn', { personName: displayName }), disabled: !canCheckIn, dataE2EId: "child-check-in-confirm-button", isOnlyButton: !nextPersonLink && isPin })] }));
};
